import * as A from 'astronomy-engine';
import { addDays, clockAt, wallTime } from '../lib/localtime';

/**
 * The Hijri date as Saudi Arabia has it, in the app and in the alerts Worker alike. The Umm al-Qurá calendar (KACST's,
 * built into every browser and into the Workers runtime) gives each month's 1st until Saudi Arabia's Supreme Court
 * decides it; its decisions (`Starts`, picked up by the server: server/watch.ts) then stand in its place. The day
 * turns at local sunset. On the evening of the 29th before a month that Saudi Arabia announces the same evening, the
 * date stays at the 29th until the announcement is in, so it never turns on a guess.
 */
export const HIJRI_MONTHS = [
  'Muḥarram', 'Ṣafar', 'Rabīʿ al-Awwal', 'Rabīʿ al-Ākhir', 'Jumādá al-Ūlá', 'Jumādá al-Ākhirah',
  'Rajab', 'Shaʿbān', 'Ramaḍān', 'Shawwāl', 'Dhū al-Qaʿdah', 'Dhū al-Ḥijjah',
];

export interface HijriMonth { month: number; year: number }
export interface HijriDay extends HijriMonth { day: number }

export const hijriName = (h: HijriMonth) => `${HIJRI_MONTHS[h.month - 1]} ${h.year}`;
/** "29 Rabīʿ al-Ākhir 1448". */
export const fmtHijri = (h: HijriDay) => `${h.day} ${hijriName(h)}`;
/** "29 Rabīʿ al-Ākhir", where the year goes without saying. */
export const fmtHijriShort = (h: HijriDay) => `${h.day} ${HIJRI_MONTHS[h.month - 1]}`;

/** Ramaḍān, Shawwāl (ʿĪd al-Fiṭr) and Dhū al-Ḥijjah: the months whose crescent people look for most. */
export const isSoughtMonth = (h: HijriMonth) => h.month === 9 || h.month === 10 || h.month === 12;
/** The months whose start Saudi Arabia announces on the evening of the 29th before them: Ramaḍān, Shawwāl,
 *  Dhū al-Ḥijjah and Muḥarram. Before these the date waits for the announcement. */
export const announcedSameEvening = (h: HijriMonth) => h.month === 9 || h.month === 10 || h.month === 12 || h.month === 1;

/** The 29th, the 30th and the 1st: the days Ahillah calls the Moon just "Crescent" (owner, 14 and 22 October 2026). */
export const isCrescentDay = (day: number) => day === 29 || day === 30 || day === 1;

export const nextMonth = (h: HijriMonth): HijriMonth => (h.month === 12 ? { month: 1, year: h.year + 1 } : { month: h.month + 1, year: h.year });
export const prevMonth = (h: HijriMonth): HijriMonth => (h.month === 1 ? { month: 12, year: h.year - 1 } : { month: h.month - 1, year: h.year });
/** "1448-09": how a month is keyed in `Starts` and by the server. */
export const monthKey = (h: HijriMonth) => `${h.year}-${String(h.month).padStart(2, '0')}`;
export const monthOfKey = (k: string): HijriMonth => ({ year: Number(k.slice(0, 4)), month: Number(k.slice(5, 7)) });

/** Days from one calendar date (YYYY-MM-DD) to another. */
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400e3);

// ---------- Umm al-Qurá ----------

let fmt: Intl.DateTimeFormat | null = null;
const uqCache = new Map<string, HijriDay>();
/** The Umm al-Qurá date of a Gregorian calendar date (YYYY-MM-DD). */
export function uqOf(date: string): HijriDay {
  let h = uqCache.get(date);
  if (!h) {
    fmt ??= new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC' });
    const parts = Object.fromEntries(fmt.formatToParts(Date.parse(`${date}T12:00:00Z`)).map((p) => [p.type, p.value]));
    h = { day: Number(parts.day), month: Number(parts.month), year: Number(parts.year ?? parts.relatedYear) };
    if (uqCache.size > 4000) uqCache.clear();
    uqCache.set(date, h);
  }
  return h;
}


const gregCache = new Map<string, string>();
/** The Gregorian date (YYYY-MM-DD) of an Umm al-Qurá date. */
export function gregOf(h: HijriDay): string {
  const key = `${h.year}-${h.month}-${h.day}`;
  const hit = gregCache.get(key);
  if (hit) return hit;
  // A first guess from the mean lengths of the year and the month (1 Muḥarram 1 AH was 19 July 622), then put right a
  // few days at a time by the calendar itself.
  const index = (x: HijriDay) => (x.year - 1) * 354.36667 + (x.month - 1) * 29.530589 + (x.day - 1);
  let date = new Date(Date.UTC(622, 6, 19) + Math.round(index(h)) * 86400e3).toISOString().slice(0, 10);
  for (let i = 0; i < 8; i++) {
    const off = Math.round(index(h) - index(uqOf(date)));
    if (!off) break;
    date = addDays(date, off);
  }
  // The months' real lengths differ from the mean by a day or so: settle on the very day.
  for (let i = 0; i < 4; i++) {
    const u = uqOf(date);
    if (u.year === h.year && u.month === h.month && u.day === h.day) break;
    date = addDays(date, index(u) < index(h) ? 1 : -1);
  }
  gregCache.set(key, date);
  return date;
}

// ---------- Saudi Arabia's calendar ----------

/** Saudi Arabia's decided month starts: month key ("1448-09") to the Gregorian date of its 1st ("2027-02-08"). */
export type Starts = Record<string, string>;

/**
 * The Gregorian date of a month's 1st by Saudi Arabia's calendar: as decided, or else as Umm al-Qurá has it. The months
 * not yet decided after one that began later (or earlier) than Umm al-Qurá still have 29 or 30 days each: from the
 * latest decided month in the year before, each month follows Umm al-Qurá's length where it can and is held to 29 or 30
 * days where it cannot, until the two calendars meet again.
 */
export function monthStart(h: HijriMonth, starts: Starts): string {
  const own = starts[monthKey(h)];
  if (own) return own;
  const undecided = [h];
  let start: string | undefined;
  for (let m = prevMonth(h); undecided.length <= 12; m = prevMonth(m)) {
    start = starts[monthKey(m)];
    if (start) break;
    undecided.push(m);
  }
  if (!start) start = gregOf({ ...h, day: 1 });
  else
    for (const m of undecided.reverse()) {
      const uq = gregOf({ ...m, day: 1 });
      const gap = daysBetween(start, uq);
      start = gap < 29 ? addDays(start, 29) : gap > 30 ? addDays(start, 30) : uq;
    }
  // With the month after it decided, it keeps to 29 or 30 days before that too.
  const after = starts[monthKey(nextMonth(h))];
  if (after) {
    const lo = addDays(after, -30), hi = addDays(after, -29);
    if (start < lo) start = lo;
    else if (start > hi) start = hi;
  }
  return start;
}

/** Saudi Arabia's Hijri date for a Gregorian calendar date (YYYY-MM-DD): the day it names, from sunset before. */
export function saudiDate(date: string, starts: Starts): HijriDay {
  const u = uqOf(date);
  for (const m of [nextMonth(u), u, prevMonth(u)]) {
    const s = monthStart(m, starts);
    if (s <= date) return { ...m, day: daysBetween(s, date) + 1 };
  }
  return u;
}

/** Whether Saudi Arabia has decided a month's start. */
export const isDecided = (h: HijriMonth, starts: Starts) => !!starts[monthKey(h)];

export interface Where { lat: number; lon: number; tz: string }

const sunsets = new Map<string, number>();
/** The sunset that ends a local calendar date's day at a place (sea level, standard refraction): the first after 04:00
 *  that day, which in the far north in summer comes just after midnight. Where the Sun does not set within the day (or
 *  does not rise), the day turns at 18:00 on the local clock. */
export function sunsetOn(date: string, w: Where): number {
  const key = `${date}|${w.lat.toFixed(2)}|${w.lon.toFixed(2)}|${w.tz}`;
  let t = sunsets.get(key);
  if (t === undefined) {
    const from = wallTime(date, w.tz, 4);
    const r = A.SearchRiseSet(A.Body.Sun, new A.Observer(w.lat, w.lon, 0), -1, new Date(from), 1);
    t = r ? r.date.getTime() : wallTime(date, w.tz, 18);
    if (sunsets.size > 2000) sunsets.clear();
    sunsets.set(key, t);
  }
  return t;
}

/** The date whose sunset is the next to come at `t`: the date on the clock, or the day before between midnight and a
 *  sunset that comes after it (the far north in summer). A reminder for "an hour before sunset" belongs to that date,
 *  even when it goes off late, a few minutes after midnight (Astra's pass 2, N1). */
export function sunsetDay(t: number, w: Where): string {
  const civil = clockAt(t, w.tz).date;
  const before = addDays(civil, -1);
  return t < sunsetOn(before, w) ? before : civil;
}

/** When a reminder dated by Saudi Arabia's calendar goes: on the Gregorian date of day `d` of month `m` of year `y`, at
 *  `h` o'clock on the local clock, or with `h` -1 an hour before sunset (by 16:00 at the latest). The phone sets it so;
 *  the server works it out again just before it goes, from the starts decided since (server/alerts.ts). */
export interface HijriTime { y: number; m: number; d: number; h: number }
export function hijriTime(x: HijriTime, starts: Starts, w: Where): number {
  const date = addDays(monthStart({ month: x.m, year: x.y }, starts), x.d - 1);
  return x.h >= 0 ? wallTime(date, w.tz, x.h) : Math.min(wallTime(date, w.tz, 16), sunsetOn(date, w) - 3600e3);
}

/** Until when the 29th waits for Saudi Arabia's announcement: 09:00 in Riyadh the next morning, or noon the next day
 *  on the local clock where that comes sooner (the far east, whose evening begins hours before Riyadh's, so that the
 *  1st is still seen there before its own sunset ends it). Every same-evening announcement on record came by 21:30 in
 *  Riyadh; if none has reached the phone by then, Umm al-Qurá stands. */
export const holdUntil = (date29: string, tz = 'Asia/Riyadh') => Math.min(wallTime(addDays(date29, 1), 'Asia/Riyadh', 9), wallTime(addDays(date29, 1), tz, 12));

export interface HijriNow extends HijriDay {
  /** Still the 29th after its evening, waiting for Saudi Arabia's announcement of the next month. */
  held?: boolean;
}

/**
 * The Hijri date at a moment and place, as Saudi Arabia has it. It turns at local sunset. After the sunset that ends
 * the 29th of the month before Ramaḍān, Shawwāl, Dhū al-Ḥijjah or Muḥarram, it stays at the 29th (through the night
 * if need be) until Saudi Arabia's decision is in. Before any other month it turns at once, as Umm al-Qurá has it,
 * and changes if the court later decides otherwise.
 */
export function hijriAt(t: number, w: Where, starts: Starts): HijriNow {
  const date = clockAt(t, w.tz).date;
  // The date whose sunset began the Hijri day now under way: today's, else yesterday's, else (in the far north in
  // summer, between midnight and a sunset just after it) the day before's.
  const yesterday = addDays(date, -1);
  const eve = t >= sunsetOn(date, w) ? date : t >= sunsetOn(yesterday, w) ? yesterday : addDays(date, -2);
  const ending = saudiDate(eve, starts);
  if (ending.day === 29 && announcedSameEvening(nextMonth(ending)) && !isDecided(nextMonth(ending), starts) && t < holdUntil(eve, w.tz)) return { ...ending, held: true };
  return saudiDate(addDays(eve, 1), starts);
}
